import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { TenantProvider } from "@/contexts/TenantContext";
import { API } from "@/lib/api";

import Login from "@/pages/Login";
import Landing from "@/pages/Landing";
import PoweredByNariai from "@/pages/PoweredByNariai";
import AdminLayout from "@/components/AdminLayout";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import Messages from "@/pages/Messages";
import Conversations from "@/pages/admin/Conversations";
import AISettings from "@/pages/AISettings";
import SEO from "@/pages/SEO";
import Tenants from "@/pages/Tenants";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import BrandSettings from "@/pages/BrandSettings";
import PlatformDashboard from "@/pages/PlatformDashboard";
import WebsitePages from "@/pages/WebsitePages";
import PageEditor from "@/pages/PageEditor";
import SiteSettings from "@/pages/SiteSettings";
import MediaLibrary from "@/pages/MediaLibrary";
import TenantSite from "@/pages/TenantSite";
import CityPage from "@/pages/CityPage";
import CitiesIndex from "@/pages/CitiesIndex";
import ArticlePage from "@/pages/ArticlePage";
import ArticlesIndex from "@/pages/ArticlesIndex";
import Blog from "@/pages/Blog";
import BotsPage from "@/pages/BotsPage";
import Custom from "@/pages/Custom";import MissRodriguezCaseStudyPage from "@/pages/MissRodriguezCaseStudyPage";
import ClientsSplash from "@/pages/ClientsSplash";
import ClientsLogin from "@/pages/ClientsLogin";
import ClientsHub from "@/pages/ClientsHub";
import AIWorkforce from "@/pages/AIWorkforce";
import WebsiteDesignDallas from "@/pages/WebsiteDesignDallas";
import SitesIndexing, { GscOAuthCallback } from "@/pages/SitesIndexing";
import {
  AIWebsiteDesignDallas, CustomWebDesignDallas, WebsiteLaunch, CustomStudio,
  AIReceptionist, AISalesBot, SEOMonitoring, CaseLoneStar, CaseIHeartFrenchies, AboutKitAlias,
  SmallBusinessWebDesignDallas, EcommerceWebsiteDesignDallas, ContractorWebsiteDesignDallas,
  WebsiteRedesignDallas, LocalSEODallas, WebsiteCostDallas, DallasWebDesignCaseStudies,
  AIAutomationDallas, AIChatbotDallas, AISalesAgentDallas, WebDevelopmentDallas,
} from "@/pages/SeoPages";
import AboutKit from "@/pages/AboutKit";
import CommandCenter from "@/pages/CommandCenter";
import ClientPortalPreview from "@/pages/ClientPortalPreview";
import IndustryPage from "@/pages/IndustryPage";
import IndustriesIndex from "@/pages/IndustriesIndex";
import IndustryCityPage from "@/pages/IndustryCityPage";
import CountyPage from "@/pages/CountyPage";
import { AIEmployees, Automation, Reviews, Forms, Billing, Analytics } from "@/pages/Phase2Stubs";

// Scroll to top on every route change so navigating between pages always
import { installGlobalClickTracker, trackPageView } from "@/lib/analytics";

// Scroll to top on every route change AND fire a GA4 page_view.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    try { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }
    catch { window.scrollTo(0, 0); }
    // Fire GA4 page_view for the new route (site is a React SPA, so gtag.js
    // won't fire on client-side navigation without this).
    // Delayed slightly so document.title has updated for the new page.
    const t = setTimeout(() => trackPageView(pathname), 200);
    return () => clearTimeout(t);
  }, [pathname]);
  return null;
}

// Install the global click tracker (phone/email/book detection) once at boot.
function AnalyticsBootstrap() {
  useEffect(() => { installGlobalClickTracker(); }, []);
  return null;
}

// Aggressively hides the Emergent branding badge + chat bubble that the
// platform re-injects into the DOM at runtime. Runs at mount, on every DOM
// mutation, and on a 1.5s interval as a safety net.
function EmergentBadgeKiller() {
  useEffect(() => {
    const kill = () => {
      try {
        const sels = [
          "#emergent-badge",
          "a[href*='app.emergent.sh']",
          "a[href*='emergent.sh?utm_source']",
          "iframe[src*='emergent.sh']",
        ];
        sels.forEach((s) => document.querySelectorAll(s).forEach((el) => el.remove()));
        document.querySelectorAll("a").forEach((a) => {
          const t = (a.textContent || "").trim().toLowerCase();
          if (t === "made with emergent" || t.startsWith("made with emergent")) a.remove();
        });
        // Emergent chat bubble launcher — matches known Intercom-style launcher pattern
        document.querySelectorAll("[class*='emergent'][class*='chat'],[id*='emergent-chat'],[id*='emergent_chat']").forEach((el) => el.remove());
      } catch { /* ignore */ }
    };
    kill();
    const mo = new MutationObserver(kill);
    mo.observe(document.documentElement, { childList: true, subtree: true });
    const iv = setInterval(kill, 1200);
    window.addEventListener("load", kill);
    return () => { mo.disconnect(); clearInterval(iv); };
  }, []);
  return null;
}

// Hostnames that should always run the PlugitAI platform (not tenant sites)
const PLATFORM_HOSTNAMES = new Set([
  "localhost", "127.0.0.1", "0.0.0.0",
  "plugitai.com", "www.plugitai.com",
]);

function isPlatformHost(host) {
  if (!host) return true;
  if (PLATFORM_HOSTNAMES.has(host)) return true;
  // Emergent preview / dev domains
  if (host.endsWith(".preview.emergentagent.com")) return true;
  if (host.endsWith(".emergent.host")) return true;
  return false;
}

function Protected({ children, superOnly = false }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--n-bg)] text-[var(--n-text-muted)] font-mono-plex text-sm">
        Authenticating…
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  const platformRoles = ["platform_owner", "platform_admin", "super_admin"];
  if (superOnly && !platformRoles.includes(user.role)) return <Navigate to="/admin" replace />;
  return children;
}

function AdminShell({ children }) {
  return (
    <TenantProvider>
      <AdminLayout>{children}</AdminLayout>
    </TenantProvider>
  );
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  // Logged-in platform admins → Mission Control
  if (user) return <Navigate to="/admin" replace />;
  // Public visitors on the platform hostname see the PlugitAI marketing site at root.
  // Tenant custom domains (e.g. lonestarstriping.pro) are handled by HostRouter above
  // and never reach RootRedirect. Direct tenant-site preview is still at /site/:slug/*.
  return <Landing />;
}

/**
 * Root gate — resolves the incoming host to a tenant slug when a custom domain
 * is pointed at Nariai (e.g., lonestarstriping.com). Falls back to the
 * standard admin app on platform hosts.
 */
function HostRouter({ children }) {
  const [state, setState] = useState({ status: "checking", slug: null });
  const location = useLocation();
  const host = typeof window !== "undefined" ? window.location.hostname : "";

  useEffect(() => {
    let cancelled = false;
    // If we are on a platform host, no tenant resolution needed
    if (isPlatformHost(host)) {
      setState({ status: "platform", slug: null });
      return;
    }
    // Attempt to resolve tenant by hostname
    axios
      .get(`${API}/public/resolve-tenant`, { params: { host } })
      .then(({ data }) => {
        if (cancelled) return;
        setState({ status: "tenant", slug: data.slug });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: "platform", slug: null });
      });
    return () => { cancelled = true; };
  }, [host]);

  if (state.status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--n-bg)] text-[var(--n-text-muted)] font-mono-plex text-sm">
        Loading…
      </div>
    );
  }

  // Custom domain matched a tenant — render their public site at every path
  if (state.status === "tenant") {
    // The /admin subtree is still reachable from custom domains too (edge case),
    // but by default we mount TenantSite for everything.
    if (location.pathname.startsWith("/admin") || location.pathname.startsWith("/login")) {
      return children;
    }
    return <TenantSite slugOverride={state.slug} />;
  }

  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <HostRouter>
          <ScrollToTop />
          <EmergentBadgeKiller />
          <AnalyticsBootstrap />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/plugitai" element={<Landing />} />
            <Route path="/nariai" element={<Landing />} />
            <Route path="/nari-ai" element={<Landing />} />
            <Route path="/tx" element={<CitiesIndex />} />
            <Route path="/tx/:city" element={<CityPage />} />
            <Route path="/tx/county/:county" element={<CountyPage />} />
            <Route path="/learn" element={<ArticlesIndex />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/bots" element={<BotsPage />} />
            <Route path="/ai-workforce" element={<AIWorkforce />} />
            <Route path="/custom-studio/ai-workforce" element={<AIWorkforce />} />
            <Route path="/custom" element={<Custom />} />
            <Route path="/custom-studio" element={<CustomStudio />} />
            <Route path="/website-launch" element={<WebsiteLaunch />} />
            <Route path="/ai-website-design-dallas" element={<AIWebsiteDesignDallas />} />
            <Route path="/website-design-dallas" element={<WebsiteDesignDallas />} />
            <Route path="/custom-web-design-dallas" element={<CustomWebDesignDallas />} />
            <Route path="/ai-receptionist" element={<AIReceptionist />} />
            <Route path="/ai-sales-bot" element={<AISalesBot />} />
            <Route path="/seo-monitoring" element={<SEOMonitoring />} />
            <Route path="/case-studies/lone-star-striping" element={<CaseLoneStar />} />
            <Route path="/case-studies/iheartfrenchies" element={<CaseIHeartFrenchies />} />
            <Route path="/about-kit" element={<AboutKitAlias />} />
            <Route path="/small-business-web-design-dallas" element={<SmallBusinessWebDesignDallas />} />
            <Route path="/ecommerce-website-design-dallas" element={<EcommerceWebsiteDesignDallas />} />
            <Route path="/contractor-website-design-dallas" element={<ContractorWebsiteDesignDallas />} />
            <Route path="/website-redesign-dallas" element={<WebsiteRedesignDallas />} />
            <Route path="/local-seo-dallas" element={<LocalSEODallas />} />
            <Route path="/website-cost-dallas" element={<WebsiteCostDallas />} />
            <Route path="/dallas-web-design-case-studies" element={<DallasWebDesignCaseStudies />} />
            <Route path="/ai-automation-dallas" element={<AIAutomationDallas />} />
            <Route path="/business-automation-dallas" element={<AIAutomationDallas />} />
            <Route path="/ai-chatbot-dallas" element={<AIChatbotDallas />} />
            <Route path="/ai-sales-agent-dallas" element={<AISalesAgentDallas />} />
            <Route path="/web-development-dallas" element={<WebDevelopmentDallas />} />
            <Route path="/clients" element={<ClientsSplash />} />
            <Route path="/clients/login" element={<ClientsLogin />} />
            <Route path="/clients/hub" element={<ClientsHub />} />
            <Route path="/case-studies/miss-rodriguez" element={<MissRodriguezCaseStudyPage />} />
            <Route path="/learn/:slug" element={<ArticlePage />} />
            <Route path="/for" element={<IndustriesIndex />} />
            <Route path="/for/:industry" element={<IndustryPage />} />
            <Route path="/for/:industry/:city" element={<IndustryCityPage />} />
            <Route path="/about" element={<AboutKit />} />
            <Route path="/kit" element={<AboutKit />} />
            <Route path="/command-center" element={<CommandCenter />} />
            <Route path="/dashboard-demo" element={<CommandCenter />} />
            <Route path="/preview" element={<ClientPortalPreview />} />
            <Route path="/peek" element={<ClientPortalPreview />} />
            <Route path="/powered-by-nariai" element={<PoweredByNariai />} />
            <Route path="/login" element={<Login />} />
            <Route path="/site/:slug/*" element={<TenantSite />} />

            <Route path="/admin" element={<Protected><AdminShell><Dashboard /></AdminShell></Protected>} />
            <Route path="/admin/sites/:tenantId/indexing" element={<Protected><AdminShell><SitesIndexing /></AdminShell></Protected>} />
            <Route path="/auth/google-search-console" element={<GscOAuthCallback />} />
            <Route path="/admin/leads" element={<Protected><AdminShell><Leads /></AdminShell></Protected>} />
            <Route path="/admin/messages" element={<Protected><AdminShell><Messages /></AdminShell></Protected>} />
            <Route path="/admin/conversations" element={<Protected><AdminShell><Conversations /></AdminShell></Protected>} />
            <Route path="/admin/ai" element={<Protected><AdminShell><AISettings /></AdminShell></Protected>} />
            <Route path="/admin/seo" element={<Protected><AdminShell><SEO /></AdminShell></Protected>} />
            <Route path="/admin/users" element={<Protected><AdminShell><Users /></AdminShell></Protected>} />
            <Route path="/admin/brand" element={<Protected><AdminShell><BrandSettings /></AdminShell></Protected>} />
            <Route path="/admin/website" element={<Protected><AdminShell><SiteSettings /></AdminShell></Protected>} />
            <Route path="/admin/website/pages" element={<Protected><AdminShell><WebsitePages /></AdminShell></Protected>} />
            <Route path="/admin/website/pages/:pageId" element={<Protected><AdminShell><PageEditor /></AdminShell></Protected>} />
            <Route path="/admin/media" element={<Protected><AdminShell><MediaLibrary /></AdminShell></Protected>} />
            <Route path="/admin/analytics" element={<Protected><AdminShell><Analytics /></AdminShell></Protected>} />
            <Route path="/admin/automation" element={<Protected><AdminShell><Automation /></AdminShell></Protected>} />
            <Route path="/admin/reviews" element={<Protected><AdminShell><Reviews /></AdminShell></Protected>} />
            <Route path="/admin/forms" element={<Protected><AdminShell><Forms /></AdminShell></Protected>} />
            <Route path="/admin/billing" element={<Protected><AdminShell><Billing /></AdminShell></Protected>} />

            {/* Super-admin-only */}
            <Route path="/admin/platform" element={<Protected superOnly><AdminShell><PlatformDashboard /></AdminShell></Protected>} />
            <Route path="/admin/tenants" element={<Protected superOnly><AdminShell><Tenants /></AdminShell></Protected>} />
            <Route path="/admin/settings" element={<Protected superOnly><AdminShell><Settings /></AdminShell></Protected>} />

            <Route path="*" element={<DefaultTenantCatchAll />} />
          </Routes>
        </HostRouter>
      </AuthProvider>
    </BrowserRouter>
  );
}

// When REACT_APP_DEFAULT_TENANT_SLUG is set, unmatched paths render the
// tenant site (which owns every path in this deployment).
// Otherwise, fall back to the previous behavior (redirect to /).
function DefaultTenantCatchAll() {
  const defaultTenant = process.env.REACT_APP_DEFAULT_TENANT_SLUG;
  if (defaultTenant) return <TenantSite slugOverride={defaultTenant} />;
  return <Navigate to="/" replace />;
}
